import { useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  HomeIcon,
  ClockIcon,
  WalletIcon,
  DraftingIcon,
  PinIcon,
  LogoutIcon,
} from "./icons";

const NAV = [
  { path: "/",           Icon: HomeIcon,     label: "Home",       short: "Home" },
  { path: "/attendance", Icon: ClockIcon,    label: "Attendance", short: "Attend" },
  { path: "/expenses",   Icon: WalletIcon,   label: "Expenses",   short: "Expenses" },
  { path: "/drawings",   Icon: DraftingIcon, label: "Drawings",   short: "Drawings" },
  { path: "/sites",      Icon: PinIcon,      label: "Sites",      short: "Sites" },
];

function Brand({ compact = false }) {
  return (
    <Link to="/" className="flex min-h-[44px] min-w-0 items-center gap-2.5 rounded-xl">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-brand-700 font-display text-sm font-bold text-white shadow-sm">
        H
      </span>
      <span className="min-w-0">
        <span className="block truncate font-display text-sm font-bold leading-tight text-slate-900">
          HILAND Survey
        </span>
        {!compact && (
          <span className="block text-xs font-medium leading-tight text-brand-700">
            Crew Portal
          </span>
        )}
      </span>
    </Link>
  );
}

function UserChip({ user, className = "" }) {
  const initial = user?.name?.[0]?.toUpperCase() || "?";
  return (
    <div className={`flex min-w-0 items-center gap-2.5 ${className}`}>
      <span
        aria-hidden="true"
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-100 font-display text-sm font-bold text-brand-800"
      >
        {initial}
      </span>
      <span className="min-w-0">
        <span className="block truncate text-sm font-semibold leading-tight text-slate-800">
          {user?.name || "Crew"}
        </span>
        {user?.role && (
          <span className="block truncate text-xs capitalize leading-tight text-slate-500">
            {user.role}
          </span>
        )}
      </span>
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [confirmingLogout, setConfirmingLogout] = useState(false);

  // "/" must match exactly; every other tab matches its sub-routes.
  const isActive = (path) =>
    path === "/" ? location.pathname === "/" : location.pathname.startsWith(path);

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-dvh bg-slate-50">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[60] focus:rounded-xl focus:bg-brand-700 focus:px-4 focus:py-2.5 focus:text-sm focus:font-semibold focus:text-white"
      >
        Skip to content
      </a>

      {/* ── Sidebar rail: tablet & desktop ───────────────────── */}
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-60 flex-col border-r border-slate-200 bg-white pl-safe md:flex lg:w-64">
        <div className="flex h-16 items-center border-b border-slate-200 px-5">
          <Brand />
        </div>

        <nav aria-label="Main" className="flex-1 space-y-1 overflow-y-auto p-3">
          {NAV.map(({ path, Icon, label }) => {
            const active = isActive(path);
            return (
              <NavLink
                key={path}
                to={path}
                aria-current={active ? "page" : undefined}
                className={[
                  "flex items-center gap-3 rounded-xl px-3 py-3 text-sm font-medium transition-colors",
                  active
                    ? "bg-brand-50 text-brand-800"
                    : "text-slate-600 hover:bg-slate-100 hover:text-slate-900",
                ].join(" ")}
              >
                <Icon className={`h-5 w-5 shrink-0 ${active ? "text-brand-700" : "text-slate-400"}`} />
                <span className="font-display">{label}</span>
                {active && (
                  <span className="ml-auto h-1.5 w-1.5 rounded-full bg-brand-600" aria-hidden="true" />
                )}
              </NavLink>
            );
          })}
        </nav>

        <div className="border-t border-slate-200 p-3 pb-safe">
          <UserChip user={user} className="px-2 py-2" />
          <button
            onClick={() => setConfirmingLogout(true)}
            className="mt-1 flex w-full items-center gap-3 rounded-xl px-3 py-3 text-sm font-medium text-slate-600 transition-colors hover:bg-red-50 hover:text-red-700"
          >
            <LogoutIcon className="h-5 w-5 shrink-0" />
            <span className="font-display">Sign out</span>
          </button>
        </div>
      </aside>

      {/* ── Content column ───────────────────────────────────── */}
      <div className="flex min-h-dvh flex-col md:pl-60 lg:pl-64">
        {/* Mobile top bar. Hidden on md+ where the sidebar carries the brand. */}
        <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/90 pt-safe backdrop-blur-md md:hidden">
          <div className="flex h-14 items-center justify-between gap-3 px-4">
            <Brand />
            <div className="flex items-center gap-1">
              <span
                aria-hidden="true"
                className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-100 font-display text-sm font-bold text-brand-800"
              >
                {user?.name?.[0]?.toUpperCase() || "?"}
              </span>
              <button
                onClick={() => setConfirmingLogout(true)}
                aria-label="Sign out"
                className="tap flex items-center justify-center rounded-xl text-slate-500 transition-colors hover:bg-red-50 hover:text-red-600"
              >
                <LogoutIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </header>

        <main id="main" className="flex-1 pb-nav md:pb-10">
          <div
            key={location.pathname}
            className="page-enter mx-auto w-full max-w-lg px-4 py-5 sm:px-6 md:max-w-3xl md:py-8 lg:max-w-content"
          >
            {children}
          </div>
        </main>
      </div>

      {/* ── Bottom tab bar: phones only ──────────────────────── */}
      <nav
        aria-label="Main"
        className="fixed inset-x-0 bottom-0 z-40 border-t border-slate-200 bg-white/95 pb-safe shadow-nav backdrop-blur-md md:hidden"
      >
        <ul className="flex px-safe">
          {NAV.map(({ path, Icon, short, label }) => {
            const active = isActive(path);
            return (
              <li key={path} className="flex-1">
                <NavLink
                  to={path}
                  aria-current={active ? "page" : undefined}
                  aria-label={label}
                  className="tap relative flex h-16 flex-col items-center justify-center gap-1"
                >
                  {/* Absolutely positioned so the active state can't change
                      row height and shift the other tabs. */}
                  <span
                    aria-hidden="true"
                    className={`absolute inset-x-3 top-0 h-0.5 rounded-full bg-brand-700 transition-opacity ${
                      active ? "opacity-100" : "opacity-0"
                    }`}
                  />
                  <Icon
                    className={`h-6 w-6 transition-colors ${
                      active ? "text-brand-700" : "text-slate-400"
                    }`}
                  />
                  <span
                    className={`font-display text-[10px] font-semibold leading-none tracking-wide ${
                      active ? "text-brand-800" : "text-slate-500"
                    }`}
                  >
                    {short}
                  </span>
                </NavLink>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Sign-out confirmation — logging out mid-shift loses an unsaved
          check-in note, so it should not be a single stray tap. */}
      {confirmingLogout && (
        <div className="fixed inset-0 z-[60] flex items-end justify-center sm:items-center sm:p-6">
          <div
            className="fade-in absolute inset-0 bg-slate-900/50 backdrop-blur-[2px]"
            onClick={() => setConfirmingLogout(false)}
            aria-hidden="true"
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="logout-title"
            className="slide-up relative w-full rounded-t-3xl bg-white p-6 pb-safe shadow-lift sm:max-w-sm sm:rounded-3xl sm:pb-6"
          >
            <h2 id="logout-title" className="font-display text-lg font-bold text-slate-900">
              Sign out?
            </h2>
            <p className="mt-1.5 text-sm text-slate-600">
              You'll need your email and password to sign back in.
            </p>
            <div className="mt-6 flex gap-3">
              <button
                onClick={() => setConfirmingLogout(false)}
                className="h-12 flex-1 rounded-xl border border-slate-300 bg-white font-display text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={handleLogout}
                className="h-12 flex-1 rounded-xl bg-red-600 font-display text-sm font-semibold text-white transition-colors hover:bg-red-700"
              >
                Sign out
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
